import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import FindTalentsContainer from '../../components/FindTalentsContainer';
import { MetaTags } from '../../components/Metatags/Metatags';
import { CustomPagination } from '../../components/Tables/Pagination';
import { usePagination } from '../../hook/usePagination';
import { useDispatchPromise } from '../../utils/AppUtils';
import { findTalent } from '../../redux/talent/actions';
import { ITalentDiscoverResponse, ITalentFilters } from '../../redux/talent/types';
import { useSelector } from 'react-redux';
import { loadingSelector, talentsSelector } from './selector';
import { Container } from '@mui/material';

const FindTalents = () => {
    const { pagination, setPagination, handleRowsChange, handlePageChange } = usePagination(1, 12);
    const talents = useSelector(talentsSelector);
    const loading = useSelector(loadingSelector);
    const dispatch = useDispatchPromise();
    const [searchParams, setSearchParams] = useSearchParams();
    const [localFilters, setLocalFilters] = useState<ITalentFilters>({});

    // Load filters from URL on mount
    useEffect(() => {
        const params = Object.fromEntries([...searchParams]);
        const urlFilters: ITalentFilters = {};

        // Convert URL params to filters
        Object.entries(params).forEach(([key, value]) => {
            if (key === 'ageFrom' || key === 'ageTo' || key === 'rating' || key === 'page' || key === 'limit') {
                urlFilters[key] = Number(value);
            } else if (key === 'languages' || key === 'skills' || key === 'ethnicity' || key === 'language_spoken') {
                urlFilters[key] = value.split(',');
            } else if (key === 'lat' || key === 'lng') {
                urlFilters[key] = Number(value);
            } else {
                urlFilters[key as keyof ITalentFilters] = value as any;
            }
        });

        setLocalFilters(urlFilters);
    }, [searchParams]);

    const getTalents = useCallback(
        async (limit?: number, page?: number, filters?: ITalentFilters) => {
            const currentFilters = filters || localFilters;
            if (!loading) {
                const response: ITalentDiscoverResponse = (await dispatch(
                    findTalent({
                        ...currentFilters,
                        limit: limit ?? pagination.rows,
                        page: page ?? pagination.page,
                    }),
                )) as ITalentDiscoverResponse;
                const { currentPage, pageSize, totalCount } = response;
                if (currentPage) {
                    setPagination({
                        page: currentPage,
                        rows: pageSize,
                        totalCount: totalCount,
                    });
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [localFilters, pagination.rows, pagination.page, setPagination],
    );

    // Initial load
    useEffect(() => {
        getTalents();
    }, [getTalents]);

    const handleSearch = useCallback(
        (searchValue: string) => {
            const newFilters = { ...localFilters, searchTerm: searchValue };
            setLocalFilters(newFilters);

            // Always build fresh URLSearchParams from scratch
            const params = new URLSearchParams();
            Object.entries(newFilters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    if (Array.isArray(value)) {
                        params.set(key, value.join(','));
                    } else {
                        params.set(key, String(value));
                    }
                }
            });

            setSearchParams(params);
            getTalents(undefined, undefined, newFilters);
        },
        [localFilters, setSearchParams, getTalents],
    );

    const handleFilterChange = useCallback(
        (filters: ITalentFilters) => {
            setLocalFilters(filters);

            const params = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    if (Array.isArray(value)) {
                        params.set(key, value.join(','));
                    } else {
                        params.set(key, String(value));
                    }
                }
            });

            setSearchParams(params);
            getTalents(undefined, undefined, filters);
        },
        [setSearchParams, getTalents],
    );

    const handleClearFilters = () => {
        const preservedFilters: ITalentFilters = {};

        // Preserve 'profile_type' if it exists
        if (localFilters.profile_type) {
            preservedFilters.profile_type = localFilters.profile_type;
        }

        setLocalFilters(preservedFilters);

        // Create new search params with only 'profile_type'
        const params = new URLSearchParams();
        if (preservedFilters.profile_type) {
            params.set('profile_type', String(preservedFilters.profile_type));
        }

        setSearchParams(params);
        getTalents(undefined, undefined, preservedFilters);
    };

    const locationQuery = localFilters?.location ? ` in ${localFilters.location}` : '';
    const roleQuery = localFilters?.searchTerm || '';
    const seoTitle = roleQuery
        ? `Find ${roleQuery}${locationQuery} | Thspian - Discover Creative Talent`
        : `Discover Creative Talent${locationQuery} | Actors, Directors, Editors & More - Thspian`;
    const seoDescription = roleQuery
        ? `Find talented ${roleQuery}${locationQuery} on Thspian. Connect with professional actors, directors of photography, editors, makeup artists, and other creative professionals. Browse portfolios and discover your next creative collaborator.`
        : `Discover talented actors, directors of photography, editors, makeup artists, and creative professionals${locationQuery} on Thspian. Browse portfolios, connect with industry talent, and find your next creative collaborator. Thespian platform for creative talent discovery.`;
    const seoKeywords = `thespian, UGC, actors near me, director of photography near me, editors near me, makeup artist near me, creative talent, casting, film industry professionals, actors, directors, cinematographers, editors, makeup artists, creative roles, talent discovery, creative professionals${
        locationQuery ? `, ${localFilters.location} talent` : ''
    }`;

    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: seoTitle,
        description: seoDescription,
        url: `https://thspian.com/discover`,
        mainEntity: {
            '@type': 'ItemList',
            itemListElement: (talents || []).slice(0, 10).map((talent, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                item: {
                    '@type': 'Person',
                    name: talent.stage_name || 'Creative Professional',
                    jobTitle: talent.profile_type || 'Creative Professional',
                },
            })),
        },
    };

    // Google Sitelinks Search Box structured data
    const sitelinksSearchBox = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        url: 'https://thspian.com/discover',
        name: 'Thspian - Discover Creative Talent',
        potentialAction: {
            '@type': 'SearchAction',
            target: {
                '@type': 'EntryPoint',
                urlTemplate: 'https://thspian.com/discover?searchTerm={search_term_string}',
            },
            'query-input': 'required name=search_term_string',
        },
    };

    return (
        <>
            {/* Google Sitelinks Search Box structured data */}
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(sitelinksSearchBox)}</script>
            </Helmet>
            <MetaTags
                title={seoTitle}
                description={seoDescription}
                keywords={seoKeywords}
                canonicalUrl="https://thspian.com/discover"
                structuredData={structuredData}>
                <Container maxWidth={false}>
                    <FindTalentsContainer
                        talents={talents}
                        filters={localFilters}
                        totalCount={pagination.totalCount}
                        locationLabel={localFilters?.location}
                        loading={loading}
                        onSearch={handleSearch}
                        onFilterChange={handleFilterChange}
                        onClearFilters={handleClearFilters}
                        customPagination={
                            <CustomPagination
                                pagination={pagination}
                                onRowsChange={handleRowsChange}
                                onPageChange={handlePageChange}
                                rowsPerPageOptions={[12, 48, 96, 120]}
                            />
                        }
                    />
                </Container>
            </MetaTags>
        </>
    );
};

export { FindTalents };
